import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Save,
  Send,
  FileDown,
  RotateCcw,
  Pencil,
  Eye,
  Plus,
  Trash2,
  Loader2,
  Dumbbell,
  ArrowLeft,
} from "lucide-react";
import type { TrainingSession } from "@/components/trainingplan/types";

interface ExerciseRow {
  name?: string;
  sets?: number | string;
  reps?: string | number;
  load?: string | number;
  rest?: string;
  notes?: string;
  [key: string]: unknown;
}

const toExercise = (raw: Record<string, unknown>): ExerciseRow => {
  const name =
    (raw.name as string) ||
    (raw.exercise as string) ||
    (raw.exercicio as string) ||
    (raw.title as string) ||
    "";
  const sets = (raw.sets as number) ?? (raw.series as number) ?? "";
  const reps = (raw.reps as string) ?? (raw.repeticoes as string) ?? "";
  const load = (raw.load as string) ?? (raw.carga as string) ?? (raw.weight as string) ?? "";
  const rest =
    (raw.rest as string) ?? (raw.descanso as string) ?? (raw.rest_seconds as string) ?? "";
  const notes = (raw.notes as string) ?? (raw.obs as string) ?? (raw.observacao as string) ?? "";
  return { ...raw, name, sets, reps, load, rest, notes };
};

interface PlanReviewViewProps {
  planName: string;
  onPlanNameChange: (v: string) => void;
  selectedObjectives: string[];
  daysPerWeek: number;
  minutesPerSession: number;
  items: TrainingSession[];
  onItemsChange: (items: TrainingSession[]) => void;
  onSave: () => void;
  onShareWhatsApp: () => void;
  onExportPDF: () => void;
  onRestart: () => void;
  onBack?: () => void;
  saving: boolean;
  sharing: boolean;
  exporting: boolean;
  patientName: string;
}

export function PlanReviewView({
  planName,
  onPlanNameChange,
  selectedObjectives,
  daysPerWeek,
  minutesPerSession,
  items,
  onItemsChange,
  onSave,
  onShareWhatsApp,
  onExportPDF,
  onRestart,
  onBack,
  saving,
  sharing,
  exporting,
  patientName,
}: PlanReviewViewProps) {
  const [editing, setEditing] = useState(false);
  const [inlineEditing, setInlineEditing] = useState<Set<string>>(new Set());

  const toggleInlineEdit = (key: string) => {
    setInlineEditing((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group items by week
  const weeks = useMemo(() => {
    const map = new Map<number, TrainingSession[]>();
    items.forEach((it) => {
      const arr = map.get(it.week_number) || [];
      arr.push(it);
      map.set(it.week_number, arr);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([week, sessions]) => ({
        week,
        sessions: sessions.sort((a, b) => a.display_order - b.display_order),
      }));
  }, [items]);

  const updateSession = (sessionId: string | undefined, idx: number, patch: Partial<TrainingSession>) => {
    const next = items.map((it, i) => {
      const matches = sessionId ? it.id === sessionId : i === idx;
      return matches ? { ...it, ...patch } : it;
    });
    onItemsChange(next);
  };

  const updateExercise = (
    sessionIdx: number,
    exIdx: number,
    patch: Partial<ExerciseRow>,
  ) => {
    const next = items.map((it, i) => {
      if (i !== sessionIdx) return it;
      const exs = (it.exercises || []).map((ex, j) =>
        j === exIdx ? { ...ex, ...patch } : ex,
      );
      return { ...it, exercises: exs };
    });
    onItemsChange(next);
  };

  const addExercise = (sessionIdx: number) => {
    const next = items.map((it, i) => {
      if (i !== sessionIdx) return it;
      return {
        ...it,
        exercises: [
          ...(it.exercises || []),
          { name: "Novo exercício", sets: 3, reps: "10", load: "", rest: "60s", notes: "" },
        ],
      };
    });
    onItemsChange(next);
  };

  const removeExercise = (sessionIdx: number, exIdx: number) => {
    const next = items.map((it, i) => {
      if (i !== sessionIdx) return it;
      return {
        ...it,
        exercises: (it.exercises || []).filter((_, j) => j !== exIdx),
      };
    });
    onItemsChange(next);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header card */}
      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                <Dumbbell className="size-5" />
              </div>
              <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                {editing ? (
                  <Input
                    value={planName}
                    onChange={(e) => onPlanNameChange(e.target.value)}
                    className="text-lg font-semibold h-9"
                  />
                ) : (
                  <CardTitle className="text-xl">{planName || "Plano de treino"}</CardTitle>
                )}
                <p className="text-sm text-muted-foreground">
                  {patientName} · {daysPerWeek}× / semana · {minutesPerSession} min por sessão
                </p>
                {selectedObjectives.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {selectedObjectives.slice(0, 5).map((o) => (
                      <Badge key={o} variant="secondary" className="font-normal">
                        {o}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant={editing ? "default" : "outline"}
              size="sm"
              onClick={() => setEditing(!editing)}
            >
              {editing ? (
                <>
                  <Eye data-icon="inline-start" />
                  Visualizar
                </>
              ) : (
                <>
                  <Pencil data-icon="inline-start" />
                  Editar
                </>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Plan content */}
      {weeks.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nenhuma sessão encontrada no plano.
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue={`week-${weeks[0].week}`} className="w-full">
          {weeks.length > 1 && (
            <TabsList className="flex-wrap h-auto">
              {weeks.map((w) => (
                <TabsTrigger key={w.week} value={`week-${w.week}`}>
                  Semana {w.week}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
          {weeks.map((w) => (
            <TabsContent key={w.week} value={`week-${w.week}`} className="mt-4">
              <Accordion type="multiple" defaultValue={w.sessions.map((_, i) => `s-${i}`)}>
                {w.sessions.map((session, sessionListIdx) => {
                  const sessionIdx = items.indexOf(session);
                  return (
                    <AccordionItem key={session.id || `s-${sessionListIdx}`} value={`s-${sessionListIdx}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left flex-1 min-w-0">
                          <div className="flex flex-col items-center justify-center size-10 rounded-lg bg-muted shrink-0">
                            <span className="text-[10px] uppercase text-muted-foreground leading-none">
                              {session.day_of_week.slice(0, 3)}
                            </span>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium truncate">{session.session_title}</span>
                            <span className="text-xs text-muted-foreground">
                              {session.day_of_week} · {session.duration_minutes ?? "-"} min
                              {session.intensity ? ` · ${session.intensity}` : ""}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col gap-3 pt-2">
                          {editing && (
                            <div className="grid sm:grid-cols-3 gap-2">
                              <Input
                                value={session.session_title}
                                onChange={(e) =>
                                  updateSession(session.id, sessionIdx, {
                                    session_title: e.target.value,
                                  })
                                }
                                placeholder="Título"
                              />
                              <Input
                                type="number"
                                value={session.duration_minutes ?? ""}
                                onChange={(e) =>
                                  updateSession(session.id, sessionIdx, {
                                    duration_minutes: Number(e.target.value) || null,
                                  })
                                }
                                placeholder="Duração (min)"
                              />
                              <Input
                                value={session.intensity ?? ""}
                                onChange={(e) =>
                                  updateSession(session.id, sessionIdx, {
                                    intensity: e.target.value || null,
                                  })
                                }
                                placeholder="Intensidade"
                              />
                            </div>
                          )}

                          <div className="rounded-lg border overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium">Exercício</th>
                                  <th className="text-left px-3 py-2 font-medium w-16">Séries</th>
                                  <th className="text-left px-3 py-2 font-medium w-20">Reps</th>
                                  <th className="text-left px-3 py-2 font-medium w-24">Carga</th>
                                  <th className="text-left px-3 py-2 font-medium w-20">Descanso</th>
                                  <th className="w-20"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(session.exercises || []).map((rawEx, exIdx) => {
                                  const ex = toExercise(rawEx);
                                  const rowKey = `${sessionIdx}-${exIdx}`;
                                  const isRowEditing = editing || inlineEditing.has(rowKey);
                                  return (
                                    <tr key={exIdx} className="border-t">
                                      {isRowEditing ? (
                                        <>
                                          <td className="px-2 py-1.5">
                                            <Input
                                              value={ex.name}
                                              onChange={(e) =>
                                                updateExercise(sessionIdx, exIdx, {
                                                  name: e.target.value,
                                                })
                                              }
                                              className="h-8"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <Input
                                              value={String(ex.sets ?? "")}
                                              onChange={(e) =>
                                                updateExercise(sessionIdx, exIdx, {
                                                  sets: e.target.value,
                                                })
                                              }
                                              className="h-8"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <Input
                                              value={String(ex.reps ?? "")}
                                              onChange={(e) =>
                                                updateExercise(sessionIdx, exIdx, {
                                                  reps: e.target.value,
                                                })
                                              }
                                              className="h-8"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <Input
                                              value={String(ex.load ?? "")}
                                              onChange={(e) =>
                                                updateExercise(sessionIdx, exIdx, {
                                                  load: e.target.value,
                                                })
                                              }
                                              className="h-8"
                                            />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <Input
                                              value={String(ex.rest ?? "")}
                                              onChange={(e) =>
                                                updateExercise(sessionIdx, exIdx, {
                                                  rest: e.target.value,
                                                })
                                              }
                                              className="h-8"
                                            />
                                          </td>
                                          <td className="px-1">
                                            <div className="flex items-center gap-0.5">
                                              {!editing && (
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="icon"
                                                  className="size-8"
                                                  onClick={() => toggleInlineEdit(rowKey)}
                                                  title="Concluir edição"
                                                >
                                                  <Eye className="size-4" />
                                                </Button>
                                              )}
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="size-8"
                                                onClick={() => removeExercise(sessionIdx, exIdx)}
                                                title="Remover exercício"
                                              >
                                                <Trash2 className="size-4 text-destructive" />
                                              </Button>
                                            </div>
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td className="px-3 py-2 font-medium">{ex.name || "—"}</td>
                                          <td className="px-3 py-2">{ex.sets || "—"}</td>
                                          <td className="px-3 py-2">{ex.reps || "—"}</td>
                                          <td className="px-3 py-2">{ex.load || "—"}</td>
                                          <td className="px-3 py-2">{ex.rest || "—"}</td>
                                          <td className="px-1">
                                            <div className="flex items-center gap-0.5">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="size-8"
                                                onClick={() => toggleInlineEdit(rowKey)}
                                                title="Editar exercício"
                                              >
                                                <Pencil className="size-3.5" />
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="size-8"
                                                onClick={() => removeExercise(sessionIdx, exIdx)}
                                                title="Remover exercício"
                                              >
                                                <Trash2 className="size-3.5 text-destructive" />
                                              </Button>
                                            </div>
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  );
                                })}
                                {(session.exercises || []).length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={6}
                                      className="px-3 py-4 text-center text-sm text-muted-foreground"
                                    >
                                      Nenhum exercício
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addExercise(sessionIdx)}
                            className="self-start"
                          >
                            <Plus data-icon="inline-start" />
                            Adicionar exercício
                          </Button>

                          {editing ? (
                            <Textarea
                              rows={2}
                              placeholder="Observações da sessão"
                              value={session.notes ?? ""}
                              onChange={(e) =>
                                updateSession(session.id, sessionIdx, {
                                  notes: e.target.value || null,
                                })
                              }
                            />
                          ) : session.notes ? (
                            <p className="text-sm text-muted-foreground italic">{session.notes}</p>
                          ) : null}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Action bar */}
      <div className="sticky bottom-4 z-10">
        <Card className="shadow-lg">
          <CardContent className="py-3 flex flex-wrap items-center justify-between gap-2">
            <Button variant="ghost" onClick={onRestart}>
              <RotateCcw data-icon="inline-start" />
              Refazer
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              {editing && (
                <Button onClick={onSave} disabled={saving} variant="outline">
                  {saving ? (
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                  ) : (
                    <Save data-icon="inline-start" />
                  )}
                  Salvar
                </Button>
              )}
              {onBack && (
                <Button variant="outline" onClick={onBack}>
                  <ArrowLeft data-icon="inline-start" />
                  Voltar
                </Button>
              )}
              <Button variant="outline" onClick={onExportPDF} disabled={exporting}>
                {exporting ? (
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                ) : (
                  <FileDown data-icon="inline-start" />
                )}
                PDF
              </Button>
              <Button onClick={onShareWhatsApp} disabled={sharing}>
                {sharing ? (
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                ) : (
                  <Send data-icon="inline-start" />
                )}
                Enviar por WhatsApp
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
